/**
 * assets/handoffs/design_handoff_websites — Websites (domain allowlist)
 * rebuild onto Untitled UI. See DomainsPage/ for the card, table, dialogs,
 * row menu and toasts.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import PageTemplate from "./../templates/PageTemplate";
import { useDomainsStore } from "../../../hooks/redux/domains";
import { useCurrentOrganizationStore } from "../../../hooks/redux/currentOrganization";
import { useAppStore } from "../../../hooks/redux/app";
import { hasSubscription } from "../helpers/organization";
import { formatError } from "../../../helpers/errors";
import { QUOTAS, UsageFieldsMapping } from "../../../consts";
import { ToastProvider, useToast } from "../theme/untitled/Toast/ToastProvider";
import Button from "../theme/untitled/Button/Button";
import Icon from "../theme/untitled/icons/Icon";
import WebsitesCard from "./DomainsPage/WebsitesCard";
import RowMenu from "./DomainsPage/RowMenu";
import AddWebsiteDialog from "./DomainsPage/AddWebsiteDialog";
import RemoveWebsiteDialog from "./DomainsPage/RemoveWebsiteDialog";
import MajorToast from "../component/Toast/MajorToast";
import styles from "./DomainsPage/DomainsPage.module.scss";

const TOAST_CONTENT = {
  removed: (name) => ({
    title: "Website removed",
    text: name
      ? `${name} is no longer allowed to embed your widgets.`
      : "It is no longer allowed to embed your widgets.",
  }),
};

const DomainsPageContent = () => {
  const history = useHistory();
  const { domains, fetchDomains, updateDomain, deleteDomain } =
    useDomainsStore();
  const { currentOrganization } = useCurrentOrganizationStore();
  const { clearErrors } = useAppStore();
  const showToast = useToast();

  const domainsRef = useRef(domains);
  domainsRef.current = domains;

  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitError, setAddSubmitError] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removeError, setRemoveError] = useState(null);
  const [busyRowId, setBusyRowId] = useState(null);
  const [rowError, setRowError] = useState(null); // {rowId, message, retry}
  const [toast, setToast] = useState(null); // {type: 'removed', domainName}

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    return clearErrors;
  }, []);

  const orgModel = currentOrganization.model || {};
  const subscription = hasSubscription(currentOrganization);
  const domainsUsed = Number(
    orgModel.usage?.[UsageFieldsMapping[QUOTAS.domains]] || 0
  );
  const domainLimit = subscription?.quotas
    ? Number(subscription.quotas[QUOTAS.domains])
    : null;
  const domainsFree =
    domainLimit != null && domainLimit >= 0
      ? Math.max(domainLimit - domainsUsed, 0)
      : null;

  const activeCount = domains.collection.filter((d) => d.available).length;
  const pausedCount = domains.collection.length - activeCount;

  const onOpenAdd = useCallback(() => {
    setAddSubmitError(null);
    setAddOpen(true);
  }, []);
  const onCloseAdd = useCallback(() => setAddOpen(false), []);

  const onSubmitAdd = useCallback(
    async (domainValue) => {
      setAddSubmitError(null);
      await updateDomain(null, { domain: domainValue, available: 1 });
      const latest = domainsRef.current;
      if (latest.updateError) {
        setAddSubmitError(
          formatError(
            latest.updateError,
            "Could not add this website. Try again."
          ).join(" ")
        );
      } else {
        setAddOpen(false);
      }
    },
    [updateDomain]
  );

  const onOpenRemove = useCallback((domain) => {
    setRemoveError(null);
    setRemoveTarget(domain);
  }, []);
  const onCloseRemove = useCallback(() => setRemoveTarget(null), []);

  const onConfirmRemove = useCallback(async () => {
    const target = removeTarget;
    if (!target) return;
    setRemoveError(null);
    await deleteDomain(target.uuid);
    const latest = domainsRef.current;
    if (latest.deleteError) {
      setRemoveError(
        formatError(
          latest.deleteError,
          "Could not remove this website. Try again."
        ).join(" ")
      );
    } else {
      setRemoveTarget(null);
      setToast({ type: "removed", domainName: target.domain });
    }
  }, [removeTarget, deleteDomain]);

  const runTogglePause = useCallback(
    async (domain) => {
      const wasAvailable = !!domain.available;
      setBusyRowId(domain.uuid);
      setRowError((prev) => (prev?.rowId === domain.uuid ? null : prev));
      await updateDomain(domain.uuid, {
        ...domain,
        available: wasAvailable ? 0 : 1,
      });
      const latest = domainsRef.current;
      if (latest.updateError) {
        setRowError({
          rowId: domain.uuid,
          message: formatError(
            latest.updateError,
            "The service didn't respond. Try again in a moment."
          ).join(" "),
          retry: () => runTogglePause(domain),
        });
      } else {
        setRowError(null);
      }
      setBusyRowId(null);
    },
    [updateDomain]
  );

  const onDismissRowError = useCallback(() => setRowError(null), []);
  const onRetryRowAction = useCallback(() => rowError?.retry?.(), [rowError]);

  const onCopy = useCallback(
    (domain) => {
      navigator.clipboard.writeText(domain.domain);
      showToast({
        variant: "success",
        message: `"${domain.domain}" copied to your clipboard.`,
      });
    },
    [showToast]
  );

  const onCloseToast = useCallback(() => setToast(null), []);

  const onUpgrade = useCallback(() => {
    history.push("/organization-settings/billing/plans");
  }, [history]);

  const renderMenu = useCallback(
    (domain) => (
      <RowMenu
        domain={domain}
        onCopy={onCopy}
        onTogglePause={runTogglePause}
        onRemove={onOpenRemove}
        disabled={busyRowId === domain.uuid}
      />
    ),
    [onCopy, runTogglePause, onOpenRemove, busyRowId]
  );

  const addDisabled = domainsFree === 0;

  return (
    <PageTemplate
      sidebarId="domains"
      title="Websites"
      subtitle="Domains allowed to embed your widgets. A widget loads only on a domain listed here — anywhere else the embed is refused."
      icon={"language"}
      headerControls={[
        <Button
          key="add-website"
          iconLeading={<Icon name="Plus" />}
          onClick={onOpenAdd}
          disabled={addDisabled}
        >
          Add website
        </Button>,
      ]}
    >
      <div className={styles.page}>
        <WebsitesCard
          domains={domains.collection}
          loading={domains.fetch && !domains.collection.length}
          error={domains.fetchError}
          onRetryFetch={fetchDomains}
          activeCount={activeCount}
          pausedCount={pausedCount}
          domainsUsed={domainsUsed}
          domainLimit={domainLimit}
          domainsFree={domainsFree}
          onUpgrade={onUpgrade}
          busyRowId={busyRowId}
          rowError={rowError}
          onDismissRowError={onDismissRowError}
          onRetryRowAction={onRetryRowAction}
          renderMenu={renderMenu}
          onOpenAdd={onOpenAdd}
        />
      </div>

      <AddWebsiteDialog
        open={addOpen}
        onClose={onCloseAdd}
        onSubmit={onSubmitAdd}
        sending={domains.update}
        submitError={addSubmitError}
        existingDomains={domains.collection}
        domainsUsed={domainsUsed}
        domainLimit={domainLimit}
      />

      <RemoveWebsiteDialog
        domain={removeTarget}
        busy={!!removeTarget && domains.delete}
        errorMessage={removeError}
        onClose={onCloseRemove}
        onConfirm={onConfirmRemove}
      />

      {toast && (
        <MajorToast
          {...TOAST_CONTENT[toast.type](toast.domainName)}
          duration={4000}
          onDismiss={onCloseToast}
        />
      )}
    </PageTemplate>
  );
};

const DomainsPage = () => (
  <ToastProvider>
    <DomainsPageContent />
  </ToastProvider>
);

export default DomainsPage;
